import React from 'react';
import { Link } from 'react-router-dom';
import metricsStore from '@src/globalStore/metricsStore';
import { Tooltip } from 'antd';
import { SeverityColorsWithOpacity, SeverityColors, SeverityText } from '@utils/aiAlerts/mapping';
import { BadgeWrapper } from './styled';

export const getColumns = (tabType, isAlertPartnerAccount) => {
  switch (tabType) {
    case 'EI':
      return contentAlertColumns(isAlertPartnerAccount);
    case 'AI':
      return adAlertColumns(isAlertPartnerAccount);
    default:
      return contentAlertColumns(isAlertPartnerAccount);
  }
};

const getBadgeSeverity = (data) => {
  const severity = data.cellData;
  return (
    <BadgeWrapper
      className="color-critical"
      color={SeverityColors[severity]}
      bgColor={SeverityColorsWithOpacity[severity]}
    >
      {SeverityText[severity] ? SeverityText[severity] : 'NA'}
    </BadgeWrapper>
  );
};

const contentAlertColumns = (isAlertPartnerAccount = false) => [
  {
    title: 'Alert Id',
    width: 150,
    sortable: true,
    resizable: true,
    dataKey: 'alertId',
    key: 'alertId',
  },
  {
    title: 'Metric',
    width: 0,
    flexGrow: 1,
    sortable: true,
    resizable: true,
    dataKey: 'metricDisplayName',
    key: 'metricDisplayName',
  },
  {
    title: 'Customer',
    width: 0,
    flexGrow: 1,
    sortable: true,
    resizable: true,
    dataKey: 'accountName',
    key: 'accountName',
    hidden: !isAlertPartnerAccount,
  },
  {
    title: 'Account',
    width: 0,
    flexGrow: 1,
    sortable: true,
    resizable: true,
    dataKey: 'c3Name',
    key: 'c3Name',
    hidden: !isAlertPartnerAccount,
  },
  {
    title: 'Root Cause',
    width: 250,
    flexGrow: 1,
    sortable: true,
    resizable: true,
    dataKey: 'rootCause',
    key: 'rootCause',
    cellRenderer: (data: any) => <div dangerouslySetInnerHTML={{ __html: data.cellData }} />,
  },
  {
    title: 'Status',
    width: 150,
    sortable: true,
    resizable: true,
    dataKey: 'status',
    key: 'status',
  },
  {
    title: 'Peak Severity',
    width: 150,
    sortable: true,
    resizable: true,
    dataKey: 'peakSeverity',
    cellRenderer: (severity: string) => getBadgeSeverity(severity),
    key: 'peakSeverity',
  },
  // {
  //     title: 'Peak Value',
  //     width: 0,
  //     flexGrow: 1,
  //     sortable: true,
  //     resizable: true,
  //     dataKey: 'peakValueFormatted',
  //     key: 'peakValueFormatted',
  // },
  {
    title: 'Impacted Uniques',
    width: 150,
    sortable: true,
    resizable: true,
    dataKey: 'cumulativeImpactedUniques',
    key: 'cumulativeImpactedUniques',
  },
  {
    title: 'Duration',
    width: 150,
    sortable: true,
    resizable: true,
    dataKey: 'duration',
    key: 'duration',
  },
  {
    title: 'Time Alert Fired',
    width: 200,
    sortable: true,
    resizable: true,
    dataKey: 'eventTime',
    cellRenderer: (data: any) => {
      if (!data.rowData.areAllRCDimensionsAvailable) {
        return getTimeFiredWithTooltip(data);
      }
      if (data.rowData.alertId) {
        return getDiagnosticsLink(data);
      }
      return data.cellData;
    },
    key: 'eventTime',
  },
];

const getDiagnosticsLink = (data) => {
  const { alertId, aiAlertType, rootCauseParam, pulse5MetricKey } = data.rowData;
  let disableLink = false;
  const alertPath = (() => {
    if (aiAlertType === 'AD_ALERT') {
      return 'pre-ad-diagnostics';
    }
    if (aiAlertType === 'APP_ALERT') {
      return 'pre-app-diagnostics';
    }
    return 'pre-diagnostics';
  })();
  const { combinedMetricsKey } = metricsStore.getState();
  if (aiAlertType === 'APP_ALERT' && !combinedMetricsKey.app.includes(pulse5MetricKey)) {
    disableLink = true;
  }
  const { EI, APP } = window.conviva.modules;
  const urlPrefix = aiAlertType === 'APP_ALERT' ? APP.baseUrl : EI.baseUrl;
  if (disableLink) {
    return (
      <Tooltip title="This custom metric has been deleted.">
        <p>{data.cellData}</p>
      </Tooltip>
    );
  }

  // For https://conviva.atlassian.net/browse/CE-10023
  // To avoid error as react router can't handle special character like % inside the pathname
  // React router will automatically decode matched params inside the url hence no need to decode it in the consumer component.
  const rootCuaseSegment = encodeURIComponent(`Alert- ${rootCauseParam}`);

  return (
    <Link
      style={{ color: '#1556FF' }}
      to={`${urlPrefix}/dashboard/ai-alerts/${alertPath}/${alertId}/${rootCuaseSegment}`}
    >
      {data.cellData}
    </Link>
  );
};

const getTimeFiredWithTooltip = (data) => {
  return (
    <Tooltip title="Diagnostics are currently not supported for root-cause based on Custom Dimensions.">
      <p>{data.cellData}</p>
    </Tooltip>
  );
};

const adAlertColumns = (isAlertPartnerAccount = false) => [
  {
    title: 'Alert Id',
    width: 150,
    sortable: true,
    resizable: true,
    dataKey: 'alertId',
    key: 'alertId',
  },
  {
    title: 'Metric',
    width: 0,
    flexGrow: 1,
    sortable: true,
    resizable: true,
    dataKey: 'metricDisplayName',
    key: 'metricDisplayName',
  },
  {
    title: 'Account',
    width: 0,
    flexGrow: 1,
    sortable: true,
    resizable: true,
    dataKey: 'accountName',
    key: 'accountName',
    hidden: !isAlertPartnerAccount,
  },
  {
    title: 'Root Cause',
    width: 250,
    flexGrow: 1,
    sortable: true,
    resizable: true,
    dataKey: 'rootCause',
    key: 'rootCause',
    cellRenderer: (data: any) => <div dangerouslySetInnerHTML={{ __html: data.cellData }} />,
  },
  {
    title: 'Peak Severity',
    width: 150,
    sortable: true,
    resizable: true,
    dataKey: 'peakSeverity',
    cellRenderer: (severity: string) => getBadgeSeverity(severity),
    key: 'peakSeverity',
  },
  // {
  //     title: 'Peak Value',
  //     width: 0,
  //     flexGrow: 1,
  //     sortable: true,
  //     resizable: true,
  //     dataKey: 'peakValueFormatted',
  //     key: 'peakValueFormatted',
  // },
  {
    title: 'Impacted Uniques',
    width: 150,
    sortable: true,
    resizable: true,
    dataKey: 'cumulativeImpactedUniques',
    key: 'cumulativeImpactedUniques',
  },
  {
    title: 'Time Alert Fired',
    width: 200,
    sortable: true,
    resizable: true,
    dataKey: 'eventTime',
    cellRenderer: (data: any) => {
      if (data.rowData.alertId) {
        return getDiagnosticsLink(data);
      }
      return data.cellData;
    },
    key: 'eventTime',
  },
];

export const rowRenderer = ({ cells, rowData, depth }) => {
  if (rowData.renderer && depth !== 0) {
    const style = {
      border: 'none !important',
    };
  }
  return cells;
};

export const defaultSort = { key: 'eventTime', order: 'desc' };
